import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import Seo from "@/components/Seo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (u.must_change_password) nav("/change-password");
      else if (u.mfa_required && !u.mfa_enabled) nav("/setup-mfa");
      else if (!u.onboarding_completed && !u.onboarding_bypass) nav("/onboarding-portal");
      else nav("/app/pipeline");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid email or password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--cc-bg)" }}>
      <Seo title="Staff sign in | Rabah Concept Recruitment" noindex />
      {/* Left: brand */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden" style={{ background: "var(--cc-primary)" }}>
        <div className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/d2b4d0f4-2491-4609-a166-4d86ae3c1a7f/images/96885bf08fa56c79ff68f1f01130c5c69280e0b3c8c2b42ba7dc34aa9bf9009c.png')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full text-white">
          <div className="flex items-center gap-3" data-testid="brand">
            <img src="/rabah-logo.png" alt="Rabah Concept" className="w-12 h-12 object-contain bg-white/10 rounded-xl p-1" />
            <div>
              <div className="font-display text-2xl font-bold">Rabah Concept</div>
              <div className="text-xs opacity-70 uppercase tracking-[0.2em]">Recruitment</div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="label-eyebrow text-white/70">CQC Safer Recruitment</div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05]">
              Build trusted<br/>care teams,<br/>
              <span style={{ color: "var(--cc-secondary)" }}>compliantly.</span>
            </h1>
            <p className="text-white/75 max-w-md text-lg leading-relaxed">
              From application to onboarding — DBS, references, Right to Work and CQC Regulation 19 in one secure workflow.
            </p>
          </div>
          <div className="text-xs text-white/50">Rabah Concept Recruitment Module v1.0</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/rabah-logo.png" alt="Rabah Concept" className="w-10 h-10 object-contain" />
            <div className="font-display text-xl font-bold">Rabah Concept</div>
          </div>

          <div className="label-eyebrow mb-3">Staff Sign in</div>
          <h2 className="font-display text-3xl font-bold mb-2">Welcome back.</h2>
          <p className="text-[var(--cc-text-soft)] mb-8">Sign in to manage your recruitment pipeline.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-eyebrow block mb-1.5">Email</label>
              <input
                type="email"
                className="cc-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Password</label>
              <input
                type="password"
                className="cc-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="login-submit-button">
              <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <Link to="/" className="text-[var(--cc-primary)] font-medium hover:underline" data-testid="back-to-portal-link">
              ← Back to candidate portal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
